// src/api.js
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://tripak.opus-international.co.uk/api";

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Auth header
export const setAuthToken = (token) => {
  if (token) {
    api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common["Authorization"];
  }
};

// Auth
export const login = (email, password) => {
  return api.post("/login", { email, password });
};

// --- Holidays / Vacations ---

// Admin: ajout d’un congé (ACCEPTÉ d’emblée)
// Le backend attend req.body.selectedUser = { userId, startDate, endDate, startDayType, endDayType }
export const addHolidayRequest = (payload) => {
  // payload shape: { userId, startDate, endDate, startDayType, endDayType }
  return api.post("/admin/add-holiday", { selectedUser: payload });
};

// User: soumettre une demande (PENDING)
export const submitHolidayRequest = (
  startDate,
  endDate,
  startDayType = "full",
  endDayType = "full"
) => {
  return api.post("/vacation-request", {
    startDate,
    endDate,
    startDayType,
    endDayType,
  });
};

// Solde congés – annuel
export const fetchVacationDays = (year) => {
  const params = {};
  if (year) params.year = year;
  return api.get("/vacation-days", { params });
};

// Mes demandes
export const fetchUserHolidayRequests = () => {
  return api.get("/user/holiday-requests");
};

// Admin: demandes en attente
export const fetchHolidayRequests = () => {
  return api.get("/admin/holiday-requests");
};

// Admin: approuver / refuser / annuler
export const approveHolidayRequest = (id) => {
  return api.post(`/admin/holiday-requests/${id}/approve`);
};

export const denyHolidayRequest = (id, reason) => {
  return api.post(`/admin/holiday-requests/${id}/deny`, { reason });
};

export const cancelHolidayRequest = (id) => {
  return api.post(`/admin/holiday-requests/${id}/cancel`);
};

// Admin: liste globale (optionnellement filtrée par statut)
export const fetchAllHolidayRequests = (status) => {
  const params = {};
  if (status) params.status = status;
  return api.get("/admin/all-holiday-requests", { params });
};

// Annulation par l’utilisateur
export const requestHolidayCancellation = (id) => {
  return api.post(`/holiday-requests/${id}/request-cancellation`);
};

// Admin: approuver/rejeter une demande d’annulation
export const approveHolidayCancellation = (id) => {
  return api.post(`/admin/holiday-requests/${id}/approve-cancellation`);
};

export const rejectHolidayCancellation = (id, reject_reason) => {
  return api.post(`/admin/holiday-requests/${id}/reject-cancellation`, {
    reject_reason,
  });
};

// Planning (calendrier accepté)
export const fetchPlanning = (startDate, endDate, department) => {
  const params = { startDate, endDate };
  if (department) params.department = department;
  return api.get("/holidays/planning", { params });
};

// Bulk admin
export const bulkHolidayRequest = (data) => {
  // data: { userIds: number[], startDate, endDate, startDayType?, endDayType? }
  return api.post("/admin/bulk-holidays", data);
};

// --- Users ---

export const fetchUsers = () => {
  return api.get("/admin/users");
};

export const createUser = (userData) => {
  // { firstName, lastName, email, password, total_vacation_hours, start_date, department_id }
  return api.post("/admin/users", userData);
};

export const updateUser = (id, updatedData) => {
  return api.put(`/admin/users/${id}`, updatedData);
};

export const deleteUser = (id) => {
  return api.delete(`/admin/users/${id}`);
};

// Public Holidays

export const fetchPublicHolidays = (year, country = "GB", region = "EW") => {
  return api.get("/public-holidays", { params: { year, country, region } });
};

export const createPublicHoliday = (payload) => {
  return api.post("/admin/public-holidays", payload);
};

export const updatePublicHoliday = (id, payload) => {
  return api.put(`/admin/public-holidays/${id}`, payload);
};

export const deletePublicHolidayAPI = (id) => {
  return api.delete(`/admin/public-holidays/${id}`);
};

// Pour les menus déroulants
export const fetchUsersForDropdown = () => {
  return api.get("/admin/users");
};

export const fetchEntitlements = (year) => {
  return api.get("/admin/entitlements", { params: { year } });
};

export const updateUserEntitlement = (userId, year, payload) => {
  return api.put(`/admin/users/${userId}/entitlements/${year}`, payload);
};

// Get user's yearly vacation balance (entitlements) for a given year
export const fetchVacationDaysForYear = (year) => {
  return api.get("/vacation-days", { params: { year } });
};

// --- Departments ---

export const fetchDepartments = () => {
  return api.get("/departments");
};

export const createDepartment = (departmentData) => {
  return api.post("/departments", departmentData);
};

export const updateDepartment = (id, updatedData) => {
  return api.put(`/departments/${id}`, updatedData);
};

export const deleteDepartment = (id) => {
  return api.delete(`/departments/${id}`);
};

// --- Sickness logs ---

export const fetchSicknessLogs = () => {
  return api.get("/sickness-logs");
};

export const createSicknessLog = (logData) => {
  return api.post("/sickness-logs", logData);
};

export const updateSicknessLog = (logId, updatedLogData) => {
  return api.put(`/sickness-logs/${logId}`, updatedLogData);
};

export const deleteSicknessLog = (logId) => {
  return api.delete(`/sickness-logs/${logId}`);
};

// --- Appointment logs ---

export const fetchAppointmentLogs = () => {
  return api.get("/appointment-logs");
};

export const createAppointmentLog = (logData) => {
  return api.post("/appointment-logs", logData);
};

export const updateAppointmentLog = (logId, updatedData) => {
  return api.put(`/appointment-logs/${logId}`, updatedData);
};

export const deleteAppointmentLog = (logId) => {
  return api.delete(`/appointment-logs/${logId}`);
};

// --- Password reset ---

export const requestResetPassword = (email) => {
  return api.post("/request-reset-password", { email });
};

export const resetPassword = (token, password) => {
  return api.post(`/reset-password/${token}`, { password });
};

export default api;
